import type { MessageDescriptor } from '@lingui/core';
import type { QueryFunction, QueryKey, UseQueryOptions } from '@tanstack/react-query';

/**
 * Brand name used as the document-title suffix and the homepage title. A proper
 * noun, so it is intentionally NOT localized (same treatment as the login
 * brand heading / logo alt text).
 */
export const SITE_NAME = 'Rossini Energy Admin';

/**
 * Query options resolving a route's dynamic display name: the entity type is
 * erased and `select` projects straight to the label text, so the header
 * breadcrumb and the document title can `useQuery` any route's label without
 * knowing its entity. Build with [defineLabelQuery].
 */
export type LabelQueryOptions = UseQueryOptions<unknown, Error, string>;

/**
 * The structural subset of a query-factory result that [defineLabelQuery]
 * type-checks. Only these fields are typed, but the WHOLE object is spread
 * through at runtime, so the entity query's cache behaviour (staleTime, retry,
 * …) intentionally carries over to the label query. `queryFn`'s key generic is
 * `never` so every concrete factory result assigns (the query-key generic is
 * branded per factory; a `never`-keyed context is the one type they all
 * accept contravariantly). A factory's own `select` is typed so `toText`
 * composes AFTER it — the label sees the factory's public (projected)
 * contract, never raw `queryFn` output the other consumers don't see.
 */
type EntityQueryOptions<TRaw, TData> = {
  queryKey: QueryKey;
  queryFn?: QueryFunction<TRaw, never> | undefined;
  select?: ((raw: TRaw) => TData) | undefined;
};

/**
 * Builds a route `labelQuery` from an entity query + a text projection,
 * spreading the source options so cache behaviour (key, staleTime, …) is
 * shared with the loader-primed entity query. If the factory projects its data
 * with its own `select`, `toText` receives the projected shape (the factory's
 * public contract): the two are composed, not overwritten.
 */
export function defineLabelQuery<TRaw, TData>(
  entityQuery: EntityQueryOptions<TRaw, TData>,
  toText: (data: TData) => string,
): LabelQueryOptions {
  const project = entityQuery.select;
  // Sole erasure site: TanStack's branded, invariant query-key generics make
  // these assertions inexpressible structurally. Sound by construction —
  // `raw` is what `queryFn` resolved, and with no factory `select` the
  // projected type IS the raw type, so `raw as TData` holds on that branch.
  return {
    ...entityQuery,
    select: (raw) => toText(project ? project(raw as TRaw) : (raw as TData)),
  } as LabelQueryOptions;
}

declare module '@tanstack/react-router' {
  // noinspection JSUnusedGlobalSymbols
  interface StaticDataRouteOption {
    /**
     * Static, localizable route name. Routes that declare it appear in the
     * header breadcrumb and provide the document title (closest labelled
     * match wins, leaf → root). Declaring it on a **layout** route labels
     * every child page's ancestor crumb (index routes need no entry).
     * Resolve at render with `useLingui()`'s `t(descriptor)` so labels stay
     * reactive to locale changes. Routes without any label are simply
     * skipped: no crumb, fallback title.
     */
    label?: MessageDescriptor;
    /**
     * Whether the crumb links to the route's pathname when it is an ancestor
     * (default true). Set `false` on layout-only labels whose pathname has no
     * page of its own (e.g. the `/park` layout — there is no parks list page),
     * so the crumb renders as plain text.
     */
    labelLinkable?: boolean;
    /**
     * Dynamic route name resolved from a query (typically loader-primed, so
     * it renders instantly): `(params) => defineLabelQuery(entityQuery, toText)`.
     * Mutually exclusive with `label`. NOTE: `params` is necessarily
     * stringly-typed — this augmentation is global, so it cannot see the
     * route's own params type. A misspelt key compiles and yields a dead
     * query (permanent skeleton crumb); check the name against the route
     * path when writing one.
     */
    labelQuery?: (params: Record<string, string>) => LabelQueryOptions;
  }
}
